/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { BlueprintManifest, TBlueprintManifestSchema } from "@adapptio/lib-core";
import {
	DESIGN_CONTEXT_READ_MODE,
	DesignContext,
	serializeIDTToYAML,
	TBlueprintIDTNode,
	TGetBlueprintSchemaModel
} from "@adapptio/lib-blueprint";
import {
	createEventEmitter,
	emitEvent,
	onEvent,
	removeAllEventListeners,
	UndoRedoManager
} from "@adapptio/lib-shared";
import { SessionStorageParamsResolver } from "../../../sessionStorage/Resolvers/SessionStorageParamsResolver";
import { SessionStorageService } from "../../../sessionStorage/SessionStorageService";
import { AuthProvidersParamsResolver } from "../../../authProviders/Resolvers/AuthProvidersParamsResolver";
import { AuthProvidersService } from "../../../authProviders/AuthProvidersService";
import { ActionRefResolver } from "../../../runtime/Resolvers/ActionRefResolver";
import { ResourceService } from "../../../resources/ResourceService";
import { ThemeRefResolver } from "../../../runtime/Resolvers/ThemeRefResolver";
import { Debounce } from "../../../decorators";

export type ConfigModelModel = TGetBlueprintSchemaModel<TBlueprintManifestSchema>;
export type ConfigModelOptsModel = ConfigModelModel["props"]["spec"]["items"][number]["value"];

export const DEFAULT_ENV_NAME = "default";

export class ConfigModel {
	/* Contexts */
	private readonly dCtx: DesignContext;

	/** Emitted when blueprint model has changed */
	public readonly onModelChange = createEventEmitter<void>();

	/** Blueprint model */
	private model: ConfigModelModel = null;

	/** Undo/redo manager */
	public readonly undoRedoManager = new UndoRedoManager({ historyLimit: 10 });

	/** Last snapshot to compare with current state */
	private lastSnapshot: TBlueprintIDTNode = null;

	/** Flag to prevent snapshot creation during undo/redo */
	private isApplyingSnapshot = false;

	/**
	 * Document constructor
	 */
	public constructor(
		serviceStorageSvc: SessionStorageService,
		authProvidersSvc: AuthProvidersService,
		resourceSvc: ResourceService
	) {
		this.dCtx = new DesignContext({
			resolvers: {
				sessionStorageOptions: new SessionStorageParamsResolver(serviceStorageSvc),
				authenticationOptions: new AuthProvidersParamsResolver(authProvidersSvc),
				actionRef: new ActionRefResolver(resourceSvc),
				themeRef: new ThemeRefResolver(resourceSvc)
			},
			readMode: DESIGN_CONTEXT_READ_MODE.FULL,
			enableCompletition: true
		});
		onEvent(this.dCtx.modelChangeEvent, this.handleModelChange);
	}

	private async initModel(): Promise<void> {
		this.handleModelChange();
	}

	/**
	 * Loads document
	 *
	 * @param blueprintContent Blueprint YAML contents
	 */
	public async loadDocument(blueprintContent: string): Promise<void> {
		this.model = this.dCtx.loadYAMLDocument(BlueprintManifest, blueprintContent, "local://manifest");

		await this.dCtx.waitForPendingOperations();
		await this.initModel();
	}

	private getOptsMap(): ConfigModelModel["props"]["spec"] {
		return this.model.props.spec;
	}

	public getOptsForEnvironment(envName: string): ConfigModelOptsModel | null {
		const optsMap = this.getOptsMap();

		for (let i = 0; i < optsMap?.items.length; i++) {
			const item = optsMap.items[i];
			if (item.key.value === envName) {
				return item.value;
			}
		}

		return null;
	}

	public createOptsForEnvironment(envName: string): ConfigModelOptsModel {
		const optsMap = this.getOptsMap();
		return optsMap.schema.addElement(optsMap, envName, undefined, true).value;
	}

	public getOrCreateOptsForEnvironment(envName: string): ConfigModelOptsModel {
		return this.getOptsForEnvironment(envName) || this.createOptsForEnvironment(envName);
	}

	/**
	 * Returns document as YAML
	 */
	public exportDocumentAsYAML(): string {
		if (!this.model) {
			throw new Error("Model is not initialized.");
		}

		const idt = this.model.schema.serialize(this.model, [ "$" ]);

		return serializeIDTToYAML(idt);
	}

	/**
	 * Returns design context
	 */
	public getDesignContext(): DesignContext {
		return this.dCtx;
	}

	/**
	 * Returns blueprint model
	 */
	public getModel(): ConfigModelModel {
		return this.model;
	}

	private handleModelChange = (): void => {
		this.createSnapshot();
		emitEvent(this.onModelChange);
	};

	private serializeModelToIdt(): TBlueprintIDTNode {
		return this.model.schema.serialize(this.model, [ "$" ]);
	}

	private applySnapshotToModel(snapshot: TBlueprintIDTNode): void {
		this.isApplyingSnapshot = true;
		this.model = BlueprintManifest.parse(this.dCtx, snapshot);
		this.lastSnapshot = snapshot;
		this.isApplyingSnapshot = false;
		emitEvent(this.onModelChange);
	}

	/**
	 * Creates a snapshot of the current state for undo/redo
	 */
	@Debounce(500)
	private createSnapshot(): void {
		if (!this.model || this.isApplyingSnapshot) {
			return;
		}

		const prevSnapshot = this.lastSnapshot;
		const currentSnapshot = this.serializeModelToIdt();
		this.lastSnapshot = currentSnapshot;

		if (prevSnapshot) {
			this.undoRedoManager.add({
				undo: () => this.applySnapshotToModel(prevSnapshot),
				redo: () => this.applySnapshotToModel(currentSnapshot),
				disposeUndo: () => {
					/** */
				},
				disposeRedo: () => {
					/** */
				}
			});

			emitEvent(this.onModelChange);
		}
	}

	public dispose(): void {
		if (this.model) {
			this.model.schema.destroy(this.model);
		}

		this.dCtx.destroy();

		removeAllEventListeners(this.onModelChange);
	}
}
